import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { LeaveTypesRepositoryT } from "screens/Vacations/components/LeaveTypes/domain/ports/leaveTypes.repository";
import type { LeaveTypeEntityT } from "screens/Vacations/components/LeaveTypes/domain/entities/leaveType.entity";
import type { UpdateLeaveTypeTypeFormValuesT } from "screens/Vacations/components/LeaveTypes/domain/types/leaveType.types";
import type { PolicyRepositoryT } from "shared/domain/ports/policy.repository";
import type { VacationPolicyFormValuesT } from "shared/domain/types/policy.types";
import type { VacationUiT } from "../domain/ui/vacationPolicy.model";

import { getVacationPolicy } from "../application/useCases/getVacationPolicy";
import { updateVacationPolicy } from "../application/useCases/updateVacationPolicy";

type CreateUseVacationParamsT = {
  leaveTypesRepository: LeaveTypesRepositoryT;
  policyRepository: PolicyRepositoryT;
};

type UpdateVacationMutationVariablesT = {
  type: UpdateLeaveTypeTypeFormValuesT;
  policy: VacationPolicyFormValuesT;
  policyId: string;
};

const toVacationUiModel = (domainType: LeaveTypeEntityT): VacationUiT => ({
  type: {
    id: domainType.id ?? null,
    icon: domainType.icon ?? null,
    code: domainType.code ?? null,
    name: domainType.name ?? null,
    description: domainType.description ?? null,
    active: domainType.active ?? null,
    color: domainType.color ?? null,
    isJustified: domainType.isJustified ?? null,
    incidenceTypeId: domainType.incidence?.id ?? null,
  },
  policy: {
    id: domainType.policy?.id ?? null,
    minimumEligibilityDays: domainType.policy?.minimumEligibilityDays ?? null,
    accrualMode: domainType.policy?.accrualMode ?? null,
    allowNegativeBalance: domainType.policy?.allowNegativeBalance ?? null,
    maxNegativeBalanceDays: domainType.policy?.maxNegativeBalanceDays ?? null,
    minAdvanceNoticeDays: domainType.policy?.minAdvanceNoticeDays ?? null,
    maxBackdateDays: domainType.policy?.maxBackdateDays ?? null,
    maxAdvanceDays: domainType.policy?.maxAdvanceDays ?? null,
    approvalFlow: domainType.policy?.approvalFlow ?? null,
    requiresAttachment: domainType.policy?.requiresAttachment ?? null,
  },
});

export const createUseVacation = ({
  leaveTypesRepository,
  policyRepository,
}: CreateUseVacationParamsT) => {
  return function useVacation({ colaboraInstanceId }: { colaboraInstanceId: string }) {
    const queryClient = useQueryClient();

    const vacationPolicyQuery = useQuery({
      queryKey: ["vacations/policy", { colaboraInstanceId }],
      queryFn: () => getVacationPolicy({ leaveTypesRepository, colaboraInstanceId }),
      select: toVacationUiModel,
      enabled: !!colaboraInstanceId,
    });

    const updateVacationPolicyMutation = useMutation({
      mutationFn: ({ type, policy, policyId }: UpdateVacationMutationVariablesT) =>
        updateVacationPolicy({
          leaveTypesRepository,
          policyRepository,
          type,
          policy,
          colaboraInstanceId,
          policyId,
        }),
      onSuccess: (data, { type }) => {
        queryClient.setQueryData<LeaveTypeEntityT>(
          ["vacations/policy", { colaboraInstanceId }],
          (oldData) => {
            if (!oldData) return oldData;

            return {
              ...oldData,
              icon: type.icon ?? oldData.icon,
              policy: {
                ...oldData.policy,
                ...data,
              },
            };
          },
        );
      },
    });

    return {
      ...vacationPolicyQuery,
      updateVacationPolicy: updateVacationPolicyMutation,
    };
  };
};
